/**
 * The Sankey: columns of nodes joined by ribbons whose width is the value,
 * in Thruster Explorer's look (look A, docs/plans/sankey.md) - boxes with the
 * name, the value and a share bar inside, ribbons inside each box's band,
 * dashes moving along the ribbons.
 *
 * Drawn in a uPlot `draw` hook over an invisible series, as the pie and the
 * gauge are, so grid panels, dashboard tiles, the tooltip and image export
 * need nothing of their own.
 *
 * Three rules this follows:
 *
 * 1. **Hover highlights, it does not only dim.** The node under the pointer,
 *    its ribbons and the nodes at their other ends are drawn stronger
 *    (fuller ribbons with an outline, bolder dashes, a thicker border with a
 *    glow); everything else fades. The hover is kept as an id, never an
 *    object, because the moving dashes redraw every frame.
 * 2. **One scale for the whole diagram,** so a ribbon in one column compares
 *    with one in another.
 * 3. **Nothing is drawn as nothing.** Outflow without a ribbon is a hatched
 *    stub with its value; a node with no reading is hatched and says why;
 *    the model's notes are written under the diagram, so an export has them.
 */
import uPlot from 'uplot';
import type { SankeyResponse } from '../../../api/types.ts';
import { plotState, type PlotState } from '../../../state/plot-state.ts';
import { PLOT_COLORS } from '../plot-constants.ts';
import { runtime } from '../plot-runtime.ts';
import { colorLegend } from '../plot-ui-state.ts';
import {
  buildSankeyModel,
  columnCount,
  type SankeyModel,
  type SankeyModelLink,
  type SankeyModelNode,
} from '../sankey-config.ts';
import { chartContainerSize, getCssVar, themeColors, type ThemeColors } from './chart-dom.ts';
import { escHtml } from './format.ts';
import { formatNumber } from './render-number.ts';
import { placeTooltip } from './tooltip.ts';
import { rebuildUplot } from './uplot-helpers.ts';

/* ── Layout (canvas px; pure, so it can be tested) ──────── */

export interface Rect { x: number; y: number; w: number; h: number }

export interface PlacedNode extends Rect {
  node: SankeyModelNode;
  /** Height of the value band inside the box, where the ribbons attach. */
  band: number;
}

export interface PlacedLink {
  link: SankeyModelLink;
  /** Centre of the ribbon at each end, its width at each end, and the narrower. */
  ya: number;
  yb: number;
  xa: number;
  xb: number;
  wa: number;
  wb: number;
  width: number;
}

export interface SankeyLayout {
  nodes: PlacedNode[];
  links: PlacedLink[];
  /** Hatched stubs for outflow without a ribbon. */
  stubs: Array<Rect & { node: SankeyModelNode; value: number }>;
  /** Value of the largest column, for the share each box shows. */
  whole: number;
}

const positive = (n: SankeyModelNode): number => (n.value != null && n.value > 0 ? n.value : 0);

/**
 * Place the boxes and ribbons in `box`. `minH` is the least a box may be -
 * two lines of text - and `gap` the space between boxes in a column. The scale
 * is the largest that lets the fullest column fit with every box at least
 * `minH`, at most 62 % of the height for the values (the rest is boxes'
 * padding and gaps, the Thruster Explorer proportion).
 */
export function layoutSankey(model: SankeyModel, box: Rect, m: { minH: number; gap: number; nodeW: number; px: number }): SankeyLayout {
  const cols = columnCount(model.nodes);
  const byCol = Array.from({ length: cols }, (_, k) => model.nodes.filter((n) => n.col === k));
  const totals = byCol.map((list) => list.reduce((a, n) => a + positive(n), 0));
  const whole = Math.max(1e-9, ...totals);
  const pad = 10 * m.px;
  const heights = (scale: number, minH: number): number[] =>
    byCol.map((list) => list.reduce((a, n) => a + Math.max(minH, positive(n) * scale + pad), 0) + Math.max(0, list.length - 1) * m.gap);
  let minH = m.minH;
  let scale = (Math.min(...byCol.map((list) => box.h - Math.max(0, list.length - 1) * m.gap)) * 0.62) / whole;
  for (let i = 0; i < 40 && Math.max(...heights(scale, minH)) > box.h; i++) scale *= 0.9;
  if (Math.max(...heights(scale, minH)) > box.h) {
    // Too many boxes for the height: shrink them to share it.
    const most = Math.max(...byCol.map((l) => l.length));
    minH = Math.max(4 * m.px, (box.h - (most - 1) * m.gap) / most - pad);
    scale = Math.min(scale, (minH * 0.5) / whole);
  }
  const nodeW = Math.min(m.nodeW, box.w / (cols * 1.6));
  const placed = new Map<string, PlacedNode>();
  byCol.forEach((list, k) => {
    const hs = list.map((n) => Math.max(minH, positive(n) * scale + pad));
    const total = hs.reduce((a, b) => a + b, 0) + Math.max(0, list.length - 1) * m.gap;
    let y = box.y + (box.h - total) / 2;
    const x = cols > 1 ? box.x + (k * (box.w - nodeW)) / (cols - 1) : box.x;
    list.forEach((n, i) => {
      placed.set(n.id, { node: n, x, y, w: nodeW, h: hs[i]!, band: positive(n) * scale });
      y += hs[i]! + m.gap;
    });
  });
  // Ribbons stack inside each box's band, ordered by where the other end is,
  // so they do not cross more than the structure makes them. Where a box's
  // ribbons add up to more than the box (outputs above their input - the
  // model names it), that end narrows to fit: a ribbon never leaves its box.
  const outAcc = new Map<string, number>();
  const centre = (id: string): number => { const p = placed.get(id)!; return p.y + p.h / 2; };
  const drawn = model.links.filter((l) => l.value > 0 && placed.has(l.from) && placed.has(l.to));
  const width = (l: SankeyModelLink): number => Math.max(1.5 * m.px, l.value * scale);
  const ya = new Map<SankeyModelLink, number>();
  const yb = new Map<SankeyModelLink, number>();
  const wa = new Map<SankeyModelLink, number>();
  const wb = new Map<SankeyModelLink, number>();
  const stack = (p: PlacedNode, list: SankeyModelLink[], at: Map<SankeyModelLink, number>, w: Map<SankeyModelLink, number>): number => {
    const sum = list.reduce((a, l) => a + width(l), 0);
    const fit = sum > p.band + 0.5 * m.px ? Math.max(p.band, 1.5 * m.px) / sum : 1;
    let acc = p.y + (p.h - p.band) / 2;
    for (const l of list) { w.set(l, width(l) * fit); at.set(l, acc + (width(l) * fit) / 2); acc += width(l) * fit; }
    return acc - (p.y + (p.h - p.band) / 2);
  };
  for (const p of placed.values()) {
    const outs = drawn.filter((l) => l.from === p.node.id).sort((a, b) => centre(a.to) - centre(b.to));
    outAcc.set(p.node.id, stack(p, outs, ya, wa));
    const ins = drawn.filter((l) => l.to === p.node.id).sort((a, b) => centre(a.from) - centre(b.from));
    stack(p, ins, yb, wb);
  }
  const links: PlacedLink[] = drawn.map((l) => {
    const a = placed.get(l.from)!;
    const b = placed.get(l.to)!;
    return {
      link: l, xa: a.x + a.w, xb: b.x, ya: ya.get(l)!, yb: yb.get(l)!,
      wa: wa.get(l)!, wb: wb.get(l)!, width: Math.min(wa.get(l)!, wb.get(l)!),
    };
  });
  const stubs: SankeyLayout['stubs'] = [];
  for (const [id, value] of model.unaccounted) {
    const p = placed.get(id);
    if (!p) continue;
    const y = p.y + (p.h - p.band) / 2 + (outAcc.get(id) ?? 0);
    const h = Math.max(2 * m.px, value * scale);
    stubs.push({ node: p.node, value, x: p.x + p.w + 2 * m.px, y, w: 30 * m.px, h });
  }
  return { nodes: [...placed.values()], links, stubs, whole };
}

/* ── Hover ──────────────────────────────────────────────── */

/** `n:<id>` for a node, `l:<from>><to>` for a ribbon. */
export const linkKey = (l: SankeyModelLink): string => `l:${l.from}>${l.to}`;

/** What a hover lights: the node, its ribbons and their other ends - or a ribbon and its two nodes. */
export function litBy(hover: string | null, links: SankeyModelLink[]): { nodes: Set<string>; links: Set<string> } | null {
  if (!hover) return null;
  const nodes = new Set<string>();
  const lit = new Set<string>();
  for (const l of links) {
    if (hover === linkKey(l) || hover === `n:${l.from}` || hover === `n:${l.to}`) {
      lit.add(linkKey(l));
      nodes.add(l.from);
      nodes.add(l.to);
    }
  }
  if (hover.startsWith('n:')) nodes.add(hover.slice(2));
  return { nodes, links: lit };
}

/* ── Drawing ────────────────────────────────────────────── */

interface Palette {
  text: string;
  textStrong: string;
  faint: string;
  border: string;
  surface2: string;
  warning: string;
  accent: string;
}

function palette(c: ThemeColors): Palette {
  return {
    text: c.text,
    textStrong: c.textStrong,
    faint: getCssVar('--text-faint') || c.text,
    border: getCssVar('--border') || c.grid,
    surface2: getCssVar('--surface2') || c.grid,
    warning: getCssVar('--warning') || '#e0a93a',
    accent: c.accent,
  };
}

function nodeColor(n: SankeyModelNode, p: Palette, custom: Record<string, string>): string {
  if (n.kind === 'sum') return p.accent;
  if (n.kind === 'rest') return p.text;
  return custom[n.title] ?? PLOT_COLORS[n.colorIndex % PLOT_COLORS.length] ?? '#6384ff';
}

function hatch(ctx: CanvasRenderingContext2D, r: Rect, color: string, px: number): void {
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(r.x, r.y, r.w, r.h, 5 * px);
  ctx.clip();
  ctx.strokeStyle = color;
  ctx.lineWidth = px;
  for (let i = -r.h; i < r.w; i += 6 * px) {
    ctx.beginPath();
    ctx.moveTo(r.x + i, r.y + r.h);
    ctx.lineTo(r.x + i + r.h, r.y);
    ctx.stroke();
  }
  ctx.restore();
}

function clip(ctx: CanvasRenderingContext2D, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > max) t = t.slice(0, -1);
  return `${t}…`;
}

function ribbonPath(l: PlacedLink): Path2D {
  const mx = (l.xa + l.xb) / 2;
  const ha = l.wa / 2;
  const hb = l.wb / 2;
  const p = new Path2D();
  p.moveTo(l.xa, l.ya - ha);
  p.bezierCurveTo(mx, l.ya - ha, mx, l.yb - hb, l.xb, l.yb - hb);
  p.lineTo(l.xb, l.yb + hb);
  p.bezierCurveTo(mx, l.yb + hb, mx, l.ya + ha, l.xa, l.ya + ha);
  p.closePath();
  return p;
}

const STATUS_TEXT: Record<string, string> = {
  missing: 'not on this ship',
  empty: 'no data',
  unplotted: 'plot again',
  off: 'off',
};

interface Geometry {
  layout: SankeyLayout | null;
  paths: Array<{ key: string; path: Path2D }>;
}

interface Hover { key: string | null }

function sankeyDrawHook(
  model: SankeyModel,
  c: ThemeColors,
  state: PlotState,
  hover: Hover,
  geometry: Geometry,
  brandSpace: boolean,
): (u: uPlot) => void {
  const pal = palette(c);
  const fonts = { sans: getCssVar('--font-sans') || 'sans-serif' };
  const disp = state.sankey_display;
  const fmt = (v: number): string => `${formatNumber(v, disp.decimals)}${disp.unit ? ` ${disp.unit}` : ''}`;
  return (u: uPlot): void => {
    const ctx = u.ctx;
    const px = uPlot.pxRatio || 1;
    const fs = state.style.font_size * px;
    const { left, top, width, height } = u.bbox;
    const noteH = model.notes.length ? model.notes.length * fs * 1.5 + 6 * px : 0;
    const box: Rect = {
      x: left + 6 * px, y: top + 6 * px,
      w: width - 12 * px,
      h: height - 12 * px - noteH - (brandSpace ? 28 * px : 0),
    };
    const lay = layoutSankey(model, box, { minH: fs * 3.3, gap: 10 * px, nodeW: 200 * px, px });
    geometry.layout = lay;
    geometry.paths = [];
    const lit = litBy(hover.key, model.links);
    const byId = new Map(lay.nodes.map((p) => [p.node.id, p]));
    const t = performance.now() / 1000;
    ctx.save();
    ctx.beginPath();
    ctx.rect(left, top, width, height);
    ctx.clip();

    // Ribbons, under the boxes.
    for (const l of lay.links) {
      const key = linkKey(l.link);
      const path = ribbonPath(l);
      geometry.paths.push({ key, path });
      const owner = byId.get(l.link.owner)!.node;
      const col = nodeColor(owner, pal, state.custom_colors);
      const isLit = !!lit?.links.has(key);
      const on = !lit || isLit;
      const rest = owner.kind === 'rest';
      ctx.fillStyle = col;
      ctx.globalAlpha = isLit ? (rest ? 0.5 : 0.66) : on ? (rest ? 0.26 : 0.4) : 0.06;
      ctx.fill(path);
      if (isLit) {
        ctx.globalAlpha = 0.95;
        ctx.strokeStyle = col;
        ctx.lineWidth = 1.4 * px;
        ctx.stroke(path);
      }
      if (disp.motion && on && !rest) {
        const speed = (20 + (60 * l.link.value) / lay.whole) * px;
        const mx = (l.xa + l.xb) / 2;
        ctx.globalAlpha = isLit ? 0.95 : 0.65;
        ctx.strokeStyle = col;
        ctx.lineWidth = Math.min((isLit ? 4 : 3) * px, Math.max(px, l.width * 0.25) + (isLit ? px : 0));
        ctx.setLineDash([6 * px, 12 * px]);
        ctx.lineDashOffset = -((t * speed) % (18 * px));
        ctx.beginPath();
        ctx.moveTo(l.xa, l.ya);
        ctx.bezierCurveTo(mx, l.ya, mx, l.yb, l.xb, l.yb);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }
    ctx.globalAlpha = 1;

    // Outflow with no ribbon: hatched, with its value.
    for (const s of lay.stubs) {
      ctx.globalAlpha = !lit || lit.nodes.has(s.node.id) ? 1 : 0.28;
      hatch(ctx, s, pal.warning, px);
      ctx.font = `600 ${fs * 0.8}px ${fonts.sans}`;
      ctx.fillStyle = pal.warning;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      // A halo in the surface colour keeps the words readable over a ribbon.
      const label = `no ribbon · ${fmt(s.value)}`;
      ctx.lineWidth = 4 * px;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = c.surface;
      ctx.strokeText(label, s.x + s.w + 6 * px, s.y + s.h / 2);
      ctx.fillText(label, s.x + s.w + 6 * px, s.y + s.h / 2);
    }
    ctx.globalAlpha = 1;

    // Boxes.
    for (const p of lay.nodes) {
      const n = p.node;
      const col = nodeColor(n, pal, state.custom_colors);
      const isLit = !!lit?.nodes.has(n.id);
      const focus = hover.key === `n:${n.id}` || (isLit && !!hover.key?.startsWith('l:'));
      ctx.globalAlpha = !lit || isLit ? 1 : 0.28;
      const r = 6 * px;
      const bad = n.status === 'missing' || n.status === 'empty' || n.status === 'unplotted' || n.status === 'negative';
      ctx.beginPath();
      ctx.roundRect(p.x, p.y, p.w, p.h, r);
      if (bad) {
        ctx.fillStyle = pal.surface2;
        ctx.fill();
        hatch(ctx, p, pal.border, px);
        ctx.beginPath();
        ctx.roundRect(p.x, p.y, p.w, p.h, r);
        ctx.strokeStyle = n.status === 'negative' ? pal.warning : pal.border;
        ctx.lineWidth = 1.2 * px;
        ctx.stroke();
      } else if (n.status === 'off') {
        ctx.fillStyle = pal.surface2;
        ctx.fill();
        ctx.strokeStyle = pal.border;
        ctx.lineWidth = px;
        ctx.stroke();
      } else {
        const base = ctx.globalAlpha;
        ctx.fillStyle = col;
        ctx.globalAlpha = base * (n.kind === 'sum' ? 0.1 : 0.16);
        ctx.fill();
        ctx.globalAlpha = base;
        ctx.strokeStyle = col;
        ctx.lineWidth = ((n.kind === 'sum' ? 2 : 1.2) + (focus ? 1.4 : 0)) * px;
        if (focus) { ctx.shadowColor = col; ctx.shadowBlur = 12 * px; }
        ctx.stroke();
        ctx.shadowBlur = 0;
        // Share bar along the bottom edge: this node's part of the largest column.
        ctx.save();
        ctx.beginPath();
        ctx.roundRect(p.x, p.y, p.w, p.h, r);
        ctx.clip();
        ctx.fillStyle = col;
        ctx.globalAlpha = base * 0.32;
        ctx.fillRect(p.x, p.y + p.h - 4 * px, p.w * Math.min(1, positive(n) / lay.whole), 4 * px);
        ctx.restore();
      }
      const inner = p.w - 18 * px;
      const twoLines = p.h >= fs * 2.6;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      ctx.font = `650 ${fs * 0.72}px ${fonts.sans}`;
      ctx.fillStyle = n.status === 'ok' ? pal.text : pal.faint;
      if (twoLines) ctx.fillText(clip(ctx, n.title.toUpperCase(), inner), p.x + 9 * px, p.y + fs * 0.95);
      const valueText = n.status === 'ok' || n.status === 'negative' ? fmt(n.value ?? 0) : STATUS_TEXT[n.status] ?? '';
      const vy = twoLines ? p.y + p.h - fs * 1.15 : p.y + p.h / 2;
      ctx.font = `700 ${fs * (n.status === 'ok' ? 1.15 : 0.9)}px ${fonts.sans}`;
      ctx.fillStyle = n.status === 'ok' ? pal.textStrong : pal.faint;
      const label = twoLines ? valueText : `${n.title} · ${valueText}`;
      const valueW = ctx.measureText(label).width;
      ctx.fillText(clip(ctx, label, inner), p.x + 9 * px, vy);
      if (n.status === 'ok' && twoLines && n.kind !== 'sum') {
        const share = `${Math.round((100 * positive(n)) / lay.whole)} %`;
        ctx.font = `400 ${fs * 0.8}px ${fonts.sans}`;
        if (valueW + ctx.measureText(share).width + 14 * px < inner) {
          ctx.fillStyle = pal.text;
          ctx.textAlign = 'right';
          ctx.fillText(share, p.x + p.w - 9 * px, vy);
        }
      }
    }
    ctx.globalAlpha = 1;

    // Notes under the diagram, in the image as well as on screen.
    if (model.notes.length) {
      ctx.font = `400 ${fs * 0.9}px ${fonts.sans}`;
      ctx.fillStyle = pal.warning;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      model.notes.forEach((note, i) => {
        ctx.fillText(clip(ctx, note, width - 16 * px), left + 8 * px, box.y + box.h + 6 * px + fs * 1.5 * (i + 0.5));
      });
    }
    ctx.restore();
  };
}

/** Node or ribbon under the cursor: boxes first, then ribbons, front to back. */
function hitAt(u: uPlot, geometry: Geometry): string | null {
  const lay = geometry.layout;
  const left = u.cursor.left;
  const top = u.cursor.top;
  if (!lay || left == null || top == null || left < 0 || top < 0) return null;
  const d = uPlot.pxRatio || 1;
  const x = left * d + u.bbox.left;
  const y = top * d + u.bbox.top;
  for (const p of lay.nodes) if (x >= p.x && x <= p.x + p.w && y >= p.y && y <= p.y + p.h) return `n:${p.node.id}`;
  for (let i = geometry.paths.length - 1; i >= 0; i--) {
    const g = geometry.paths[i]!;
    if (u.ctx.isPointInPath(g.path, x, y)) return g.key;
  }
  return null;
}

function sankeyTooltipHook(
  model: SankeyModel,
  state: PlotState,
  hover: Hover,
  geometry: Geometry,
  target?: { el: HTMLElement; container: HTMLElement },
): (u: uPlot) => void {
  const disp = state.sankey_display;
  const fmt = (v: number): string => `${formatNumber(v, disp.decimals)}${disp.unit ? ` ${disp.unit}` : ''}`;
  const byId = new Map(model.nodes.map((n) => [n.id, n]));
  return (u: uPlot): void => {
    const tt = target?.el ?? runtime.tooltipEl;
    const key = u.over ? hitAt(u, geometry) : null;
    if (key !== hover.key) {
      hover.key = key;
      u.redraw(false, false);
    }
    if (!tt) return;
    if (!key) {
      tt.classList.remove('visible');
      tt.dataset.ttIdx = '';
      return;
    }
    if (tt.dataset.ttIdx !== `sankey:${key}`) {
      const row = (name: string, val: string): string =>
        `<div class="uptt-row"><span class="uptt-name">${escHtml(name)}</span><span class="uptt-val">${escHtml(val)}</span></div>`;
      const rows: string[] = [];
      if (key.startsWith('n:')) {
        const n = byId.get(key.slice(2))!;
        rows.push(`<div class="uptt-time">${escHtml(n.title)}</div>`);
        rows.push(row('value', n.value != null ? fmt(n.value) : STATUS_TEXT[n.status] ?? '—'));
        if (n.kind === 'sum') rows.push(row('is', 'the sum of its inputs'));
        if (n.kind === 'rest') rows.push(row('is', 'input − other outputs'));
        if (n.missing.length) rows.push(row('not on this ship', n.missing.join(', ')));
      } else {
        const l = model.links.find((x) => linkKey(x) === key)!;
        const a = byId.get(l.from)!;
        const b = byId.get(l.to)!;
        rows.push(`<div class="uptt-time">${escHtml(a.title)} → ${escHtml(b.title)}</div>`);
        rows.push(row('flow', fmt(l.value)));
        if (a.value) rows.push(row(`of ${a.title}`, `${Math.round((100 * l.value) / a.value)} %`));
        if (b.value) rows.push(row(`of ${b.title}`, `${Math.round((100 * l.value) / b.value)} %`));
      }
      tt.innerHTML = rows.join('');
      tt.dataset.ttIdx = `sankey:${key}`;
    }
    tt.classList.add('visible');
    const container = target?.container ?? runtime.containerEl;
    if (container) placeTooltip(u, tt, container);
  };
}

/** What the diagram says, for a screen reader and the hover title. */
export function describeFlows(model: SankeyModel, fmt: (v: number) => string): string {
  const byId = new Map(model.nodes.map((n) => [n.id, n]));
  const flows = model.links.map((l) => `${byId.get(l.from)!.title} to ${byId.get(l.to)!.title} ${fmt(l.value)}`);
  return flows.length ? `Sankey: ${flows.join(', ')}` : 'Sankey';
}

/**
 * uPlot options for a Sankey. `size`, the tooltip target and `state` let a
 * grid panel or a dashboard tile reuse this drawing. The moving dashes run
 * while the chart is on the page and stop with it.
 */
export function buildSankeyOpts(
  data: SankeyResponse,
  c: ThemeColors,
  size: { width: number; height: number },
  target?: { el: HTMLElement; container: HTMLElement },
  state: PlotState = plotState.value,
  brandSpace = false,
): { opts: uPlot.Options; uData: uPlot.AlignedData } {
  const disp = state.sankey_display;
  const fmt = (v: number): string => `${formatNumber(v, disp.decimals)}${disp.unit ? ` ${disp.unit}` : ''}`;
  const model = buildSankeyModel(state.sankey_nodes, data.dials, fmt);
  const hover: Hover = { key: null };
  const geometry: Geometry = { layout: null, paths: [] };
  const hidden = { show: false } as uPlot.Axis;
  const accessible = describeFlows(model, fmt);
  const animate = (u: uPlot): void => {
    if (!disp.motion || !model.links.some((l) => l.value > 0)) return;
    const tick = (): void => {
      if (!u.root.isConnected) return;
      u.redraw(false, false);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  return {
    uData: [[0, 1], [null, null]],
    opts: {
      ...size,
      padding: [4, 4, 4, 4],
      scales: { x: { time: false, range: () => [0, 1] }, y: { range: () => [0, 1] } },
      axes: [hidden, { ...hidden }],
      series: [{}, { label: 'sankey', paths: () => null, points: { show: false } }],
      legend: { show: false },
      cursor: { x: false, y: false, points: { show: false }, drag: { x: false, y: false } },
      hooks: {
        draw: [sankeyDrawHook(model, c, state, hover, geometry, brandSpace)],
        setCursor: [sankeyTooltipHook(model, state, hover, geometry, target)],
        ready: [(u) => {
          u.root.setAttribute('role', 'img');
          u.root.setAttribute('aria-label', accessible);
          // The page's first paint may come before the hook runs; start once on the page.
          requestAnimationFrame(() => animate(u));
        }],
      },
    },
  };
}

export function renderSankey(data: SankeyResponse): void {
  const { opts, uData } = buildSankeyOpts(data, themeColors(), chartContainerSize(), undefined, plotState.value, true);
  rebuildUplot(uData, opts);
  colorLegend.set(null);
}
